/**
 * The shared display's live signal (#582 slice 5): one Socket.IO connection, listen-only.
 *
 * **Why this is not `socket.io-client`.** The Lantern app does not depend on it (`app/package.json`
 * lists React and the router only), and a new runtime dependency needs the owner's say
 * (CLAUDE.md, "Stop and ask the owner"). The browser's own WebSocket covers what a board needs, because a board only
 * LISTENS: the server sends one board-safe event, `board.changed`, into the household's device
 * room, and the board refetches `GET /api/dashboard-data`. So this speaks the small part of the
 * Engine.IO 4 / Socket.IO 5 wire format that a listener uses, over the websocket transport only:
 *
 *   server → `0{...}`  open (ping interval and timeout)     client → `40`   join the default namespace
 *   server → `40{...}` joined                               server → `44{...}` refused by the server
 *   server → `2`       ping                                 client → `3`    pong
 *   server → `42[...]` an event                             server → `41` / `1` closed
 *
 * ponytail: websocket transport only, text packets only. No polling fallback and no binary
 * attachments — `board.changed` needs neither. If a proxy ever blocks websockets the board shows
 * itself stale rather than live, which is the honest outcome; switch to `socket.io-client` (with
 * the owner's approval) if the board ever needs to SEND, or needs polling.
 *
 * `server/realtime/__tests__/582-device-room.test.js` drives this module against a REAL Socket.IO
 * server, so the wire format above is measured rather than remembered.
 *
 * The session cookie is the only credential: the browser sends it on the upgrade request. Nothing
 * here reads, stores or sends a token. The display must share the API's registrable domain: its
 * SameSite=Lax session cookie is absent on cross-site upgrades, which look like an unpaired display.
 */

const API_ORIGIN = import.meta.env?.VITE_API_ORIGIN ?? '';
const RETRY_STEPS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

/** The websocket URL of the Socket.IO endpoint on the API origin (or on this page's origin). */
export function boardSocketUrl(origin = API_ORIGIN, location = globalThis.location) {
  const base = origin || (location ? `${location.protocol}//${location.host}` : '');
  const ws = base.replace(/^http(s?):/i, (_m, s) => `ws${s}:`);
  return `${ws}/socket.io/?EIO=4&transport=websocket`;
}

/**
 * @param {object} [options]
 * @param {string} [options.url]            full websocket URL; defaults to `boardSocketUrl()`
 * @param {typeof WebSocket} [options.WebSocketImpl]
 * @param {boolean} [options.reconnect=true]
 * @returns {{ on(event: string, fn: Function): () => void, close(): void, readonly connected: boolean }}
 *   Events: `connect` (joined), `disconnect` (lost), `connect_error` (the server refused, with its
 *   message), and every server event by its own name.
 */
export function createBoardSocket({
  url = boardSocketUrl(),
  WebSocketImpl = globalThis.WebSocket,
  reconnect = true,
} = {}) {
  const listeners = new Map();
  let ws = null;
  let connected = false;
  let closed = false;
  let attempt = 0;
  let retryTimer = null;
  let livenessTimer = null;
  let liveness = 45_000;

  function fire(event, ...args) {
    for (const fn of [...(listeners.get(event) ?? [])]) {
      try {
        fn(...args);
      } catch {
        // A listener that throws must not stop the others, or the socket.
      }
    }
  }

  function armLiveness() {
    clearTimeout(livenessTimer);
    // No ping inside interval + timeout means the connection is dead, even if nobody said so.
    livenessTimer = setTimeout(() => ws?.close(), liveness);
  }

  function lost() {
    clearTimeout(livenessTimer);
    const was = connected;
    connected = false;
    ws = null;
    if (was) fire('disconnect');
    if (closed || !reconnect) return;
    const wait = RETRY_STEPS_MS[Math.min(attempt, RETRY_STEPS_MS.length - 1)];
    attempt += 1;
    retryTimer = setTimeout(open, wait);
  }

  function onPacket(socket, data) {
    if (typeof data !== 'string' || data === '') return;
    const type = data[0];
    if (type === '0') {
      try {
        const handshake = JSON.parse(data.slice(1));
        const interval = Number(handshake.pingInterval);
        const timeout = Number(handshake.pingTimeout);
        if (Number.isFinite(interval) && Number.isFinite(timeout)) liveness = interval + timeout;
      } catch {
        // Keep the default liveness bound.
      }
      armLiveness();
      socket.send('40');
      return;
    }
    if (type === '2') {
      armLiveness();
      socket.send('3');
      return;
    }
    if (type === '1') {
      socket.close();
      return;
    }
    if (type !== '4') return;
    const kind = data[1];
    if (kind === '0') {
      connected = true;
      attempt = 0;
      fire('connect');
    } else if (kind === '4') {
      let message = 'refused';
      try {
        message = JSON.parse(data.slice(2))?.message || message;
      } catch {
        // An unreadable refusal is still a refusal.
      }
      fire('connect_error', new Error(message));
      socket.close();
    } else if (kind === '1') {
      socket.close();
    } else if (kind === '2') {
      let packet;
      try {
        packet = JSON.parse(data.slice(2));
      } catch {
        return;
      }
      if (Array.isArray(packet) && typeof packet[0] === 'string') fire(packet[0], packet[1]);
    }
  }

  function open() {
    retryTimer = null;
    if (closed || typeof WebSocketImpl !== 'function') return;
    let socket;
    try {
      socket = new WebSocketImpl(url);
    } catch {
      lost();
      return;
    }
    ws = socket;
    socket.onmessage = (event) => {
      if (ws === socket) onPacket(socket, event.data);
    };
    socket.onclose = () => {
      if (ws === socket) lost();
    };
    socket.onerror = () => {
      // Every error is followed by `onclose`, which handles it.
    };
  }

  open();

  return {
    on(event, fn) {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event).add(fn);
      return () => listeners.get(event)?.delete(fn);
    },
    close() {
      closed = true;
      clearTimeout(retryTimer);
      clearTimeout(livenessTimer);
      const socket = ws;
      ws = null;
      connected = false;
      listeners.clear();
      try {
        socket?.close();
      } catch {
        // Already closing.
      }
    },
    get connected() {
      return connected;
    },
  };
}
